import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "@breakout/ui/components/Dialog"
import { ArrowDown, ArrowUp, Check, Share2, X } from "lucide-react"
import type { CSSProperties } from "react"
import { useCallback } from "react"
import { SimpleAvatar } from "../ui/SimpleAvatar"

type OutcomeDirection = "up" | "neutral" | "down"

// Grid pattern style for share card background
const GRID_PATTERN_STYLE: CSSProperties = {
	backgroundImage:
		"linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)",
	backgroundSize: "20px 20px",
}

interface ShareTradeModalProps {
	isOpen: boolean
	onClose: () => void
	/** The outcome label the user bet on (e.g., "Long" or "jessepollak falls most") */
	outcomeLabel: string
	/** The market URL to share */
	marketUrl: string
	/** Creator/market avatar URL */
	avatarUrl?: string
	/** Creator/market name for display */
	marketName?: string
	/** Creator's Twitter/X handle (without @) */
	handle?: string
	/** Direction of the bet (up/down/neutral) */
	direction?: OutcomeDirection
	/** Market question/title */
	marketTitle?: string
	/** Current probability (0-1) for the leading outcome */
	probability?: number
}

export function ShareTradeModal({
	isOpen,
	onClose,
	outcomeLabel,
	marketUrl,
	avatarUrl,
	marketName,
	handle,
	direction,
	marketTitle,
	probability,
}: ShareTradeModalProps) {
	// Build the share text for X/Twitter
	// Custom question markets (e.g., Moltbook): use question text instead of handle
	const isCustomQuestion = handle?.startsWith("custom-question")
	const shareText = isCustomQuestion && marketTitle
		? `I just bet ${outcomeLabel} on "${marketTitle}" on @BreakoutApp`
		: `I just bet ${outcomeLabel} ${handle ? `@${handle}` : (marketName ?? "this market")} on @BreakoutApp`
	// No URL in tweet - just the text
	const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`

	const handleShareOnX = useCallback(() => {
		window.open(twitterShareUrl, "_blank", "noopener,noreferrer")
	}, [twitterShareUrl])

	// Check if native share is available
	const canNativeShare = typeof navigator !== "undefined" && !!navigator.share

	const handleNativeShare = useCallback(async () => {
		if (!navigator.share) {
			return
		}
		try {
			await navigator.share({
				title: "Breakout",
				text: shareText,
				url: marketUrl,
			})
		} catch {
			// User cancelled or share failed - silently ignore
		}
	}, [shareText, marketUrl])

	// Determine if LONG or SHORT based on direction
	const isLong = direction === "up"
	const isShort = direction === "down"
	const betLabel = isLong ? "LONG" : isShort ? "SHORT" : outcomeLabel.toUpperCase()
	const ArrowIcon = isLong ? ArrowUp : isShort ? ArrowDown : null

	// Format probability as percentage
	const probabilityText = probability != null ? `${Math.round(probability * 100)}%` : null

	return (
		<Dialog
			onOpenChange={open => {
				if (!open) {
					onClose()
				}
			}}
			open={isOpen}
		>
			<DialogContent className="max-w-sm overflow-hidden border-0 p-0 sm:rounded-3xl">
				{/* Close button */}
				<DialogClose asChild>
					<button
						aria-label="Close"
						className="absolute top-3 right-3 z-10 flex size-8 items-center justify-center rounded-full bg-black/60 text-white/70 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white"
						type="button"
					>
						<X aria-hidden="true" className="size-4" />
					</button>
				</DialogClose>

				{/* Share card */}
				<div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-900 p-5">
					{/* Subtle grid pattern background */}
					<div
						className="pointer-events-none absolute inset-0 opacity-10"
						style={GRID_PATTERN_STYLE}
					/>

					{/* Top: Breakout logo */}
					<div className="relative mb-4 flex items-center justify-between">
						<img
							alt="Breakout"
							className="h-6"
							crossOrigin="anonymous"
							src="/breakout-logo@2x.png"
						/>
						{/* LONG/SHORT badge */}
						<div
							className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 font-bold text-sm ${
								isLong
									? "bg-green-500 text-white"
									: isShort
										? "bg-red-500 text-white"
										: "bg-breakout-brand text-black"
							}`}
						>
							{ArrowIcon && <ArrowIcon aria-hidden="true" className="size-4" strokeWidth={3} />}
							<span>{betLabel}</span>
						</div>
					</div>

					{/* Middle: Creator info */}
					<div className="relative flex items-center gap-3">
						{avatarUrl ? (
							<SimpleAvatar
								alt={marketName ?? "Market"}
								className="size-14 rounded-xl ring-2 ring-white/20"
								size="lg"
								src={avatarUrl}
								variant="square"
							/>
						) : (
							<div className="flex size-14 items-center justify-center rounded-xl bg-gray-800 ring-2 ring-white/20">
								<span className="font-bold text-2xl text-white">?</span>
							</div>
						)}
						<div className="min-w-0 flex-1">
							<div className="truncate font-bold text-lg text-white">{marketName ?? "Market"}</div>
							{handle && <div className="truncate text-gray-400 text-sm">@{handle}</div>}
						</div>
					</div>

					{/* Market question */}
					{marketTitle && (
						<div className="relative mt-4">
							<p className="text-pretty font-semibold text-base text-white/90 leading-snug">
								{marketTitle}
							</p>
						</div>
					)}

					{/* Probability bar */}
					{probabilityText && (
						<div className="relative mt-4">
							<div className="flex items-center justify-between text-sm">
								<span className="text-gray-400">Chance</span>
								<span className="font-bold text-green-400">{probabilityText}</span>
							</div>
							<div className="mt-1.5 h-2 overflow-hidden rounded-full bg-gray-800">
								<div
									className="h-full rounded-full bg-gradient-to-r from-green-500 to-green-400"
									style={{ width: probabilityText }}
								/>
							</div>
						</div>
					)}

					{/* Bottom: App branding */}
					<div className="relative mt-5 flex items-center justify-between border-gray-800 border-t pt-4">
						<span className="text-gray-500 text-xs">breakout.app</span>
						<span className="text-gray-500 text-xs">Trade CT mindshare</span>
					</div>
				</div>

				{/* Content section */}
				<div className="bg-bg-primary px-6 pt-4 pb-6 text-center">
					{/* Success badge */}
					<div className="mb-3 flex justify-center">
						<span className="inline-flex items-center gap-1.5 rounded-full bg-breakout-brand/20 px-3 py-1 font-semibold text-breakout-brand text-xs">
							<Check aria-hidden="true" className="size-3.5" />
							Position confirmed
						</span>
					</div>

					{/* Title */}
					<DialogTitle className="font-bold font-display text-text-primary text-xl leading-tight sm:text-2xl">
						You're in on <span className="text-breakout-brand">{outcomeLabel}</span>
					</DialogTitle>

					{/* Description */}
					<DialogDescription className="mx-auto mt-2 max-w-[280px] text-sm text-text-secondary">
						Share your prediction on X and let others know.
					</DialogDescription>

					{/* Action buttons */}
					<div className="mt-5 space-y-2.5">
						{/* Share on X button - primary action */}
						<button
							className="group flex w-full items-center justify-center gap-2.5 rounded-xl bg-breakout-brand px-4 py-3.5 font-semibold text-black shadow-glow transition-all hover:bg-breakout-brand-hover hover:shadow-glow-lg active:scale-[0.98]"
							onClick={handleShareOnX}
							type="button"
						>
							<svg aria-hidden="true" className="size-5" fill="currentColor" viewBox="0 0 24 24">
								<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
							</svg>
							<span>Share on X</span>
						</button>

						{/* Native share button - only shown if available */}
						{canNativeShare && (
							<button
								className="flex w-full items-center justify-center gap-2 rounded-xl border border-border-subtle bg-bg-card px-4 py-3 font-semibold text-text-primary transition-all hover:border-border-highlight hover:bg-bg-secondary active:scale-[0.98]"
								onClick={handleNativeShare}
								type="button"
							>
								<Share2 aria-hidden="true" className="size-4" />
								<span>Share</span>
							</button>
						)}

						{/* Continue trading link */}
						<button
							className="mt-1 w-full py-2 text-sm text-text-muted transition-colors hover:text-text-secondary"
							onClick={onClose}
							type="button"
						>
							Continue trading
						</button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
